<template>
<div>
    <CCol sm="12">
        <CCard>
            <CCardBody class="d-flex flex-column">
                <div class="d-flex flex-column">
                    <!-- Name -->
                    <div role="group" class="form-group col-12">
                        <label class=""> {{ $t('nameMandatory') }}</label>
                        <CInput type="text" v-model="board.name" :invalid-feedback="errorOutput['Name']" :is-valid="genericValidator(board.name, attemptedSubmit) && errorValidator('Name')"></CInput>
                    </div>
                    <!-- ObjFile -->
                    <div role="group" class="form-group col-12">
                        <CustomPicker v-model="board.objFile" :title="$t('objFile')" v-bind:class="{'is-invalid': genericValidator(board.objFile, attemptedSubmit) === false || errorValidator('ObjFile') }" :invalid-feedback="errorOutput['ObjFile']" mime="model/obj" accept=".obj"></CustomPicker>
                    </div>
                    <!-- MtlFile -->
                    <div role="group" class="form-group col-12">
                        <CustomPicker v-model="board.mtlFile" :title="$t('mtlFile')" v-bind:class="{'is-invalid': genericValidator(board.mtlFile, attemptedSubmit) === false || errorValidator('MtlFile') }" :invalid-feedback="errorOutput['MtlFile']" mime="model/mtl" accept=".mtl"></CustomPicker>
                    </div>
                    <!-- img texture -->
                    <div role="group" class="form-group col-12">
                        <CustomPicker v-model="board.image" :title="$t('imageTexture')" v-bind:class="{'is-invalid': genericValidator(board.image, attemptedSubmit) === false || errorValidator('Image') }" :invalid-feedback="errorOutput['Image']" mime="image/png" accept=".png"></CustomPicker>
                    </div>
                    <!-- Preview Image -->
                    <div role="group" class="form-group col-12 col-md-4">
                        <label class=""> {{ $t('previewImage') }}</label>
                        <GalleryPicker class="gallery-picker" v-model="board.previewImage" :ratioWidth="'1'" :ratioHeight="'1'" v-bind:class="{'is-invalid': genericValidator(board.previewImage, attemptedSubmit) === false || errorValidator('PreviewImage') === false }" :max="1"></GalleryPicker>
                        <div v-if="errorValidator('PreviewImage') === false" class="invalid-feedback d-block">{{ errorOutput['PreviewImage'] }}</div>
                    </div>
                </div>
                <!-- API errors that belong to no field above -->
                <FormApiErrors :fields="['Image', 'MtlFile', 'Name', 'ObjFile', 'PreviewImage']"></FormApiErrors>
                <!-- Action button + submitting -->  
                <!-- Remove v-if to activate edit -->
                <div role="group" class="form-group col-12 d-flex justify-content-end">
                    <CSpinner v-if="submitting" variant="success" small class="align-self-end"></CSpinner>
                    <CButton v-else type="button" class="btn btn-primary" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
                </div>
                <!-- Loading -->
                <FormLoading v-if="loading"></FormLoading>
            </CCardBody>
        </CCard>
    </CCol>
    <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup" v-model="showConfirmationPopup"></ConfirmationPopUp>
</div>
</template>
 
<script>
import FormApiErrors from '@/controls/FormApiErrors.vue';
import { genericValidator, errorValidator, deepClone } from "@/helpers/utils.js";
import Board from "@/classes/Board";
import { mapState, mapActions } from 'vuex'
import LanguageDropdown from "@/controls/LanguageDropdown.vue";
import { CONSTANTS } from '@/helpers/consts';
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import FormLoading from '@/controls/FormLoading.vue'
import CustomPicker from '@/controls/CustomPicker.vue'
import { API_HELPER } from "@/helpers/api.js";
import GalleryPicker from '@/controls/GalleryPicker.vue'

  export default {
  components: { FormApiErrors, LanguageDropdown, ConfirmationPopUp, FormLoading, CustomPicker, GalleryPicker },
    props:{    
        baseBoard:
        {
            type: Object,
            default: null,
        },
        loading:
        {
            type: Boolean,
            default: false,
        },
    },
    data()
    {
        return {
            attemptedSubmit: false,
            currentLanguage: CONSTANTS.supportedLanguages[0],
            board: new Board(),
            submitting: false,
            showConfirmationPopup: false,
            confirmationMessage: "",
        }
    },
    mounted: function()
    {
        this.resetBoard();
    },
    computed:
    {
        ...mapState({ 
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        errorOutput: function()
        {
            var result = [];

            if(this.alertType == 'alert-danger' && this.alertOutput && Array.isArray(this.alertOutput))
            {
                result = this.alertOutput.reduce(function(accum, currentVal) {
                accum[currentVal.key] = currentVal.value;
                    return accum;
                }, {});
            }

            // Validate required fields
            if(!this.board.name) { result['Name'] = this.$i18n.t("validateRequiredField"); }
            if(!this.board.mtlFile) { result['MtlFile'] = this.$i18n.t("validateRequiredField"); }
            if(!this.board.objFile) { result['ObjFile'] = this.$i18n.t("validateRequiredField"); }
            if(!this.board.image) { result['Image'] = this.$i18n.t("validateRequiredField"); }
            if(!this.board.previewImage) { result['PreviewImage'] = this.$i18n.t("validateRequiredField"); }

            return result;
        },
        ...mapState('boards', ['putBoardOutput']),
        ...mapState('boards', ['postBoardOutput']),
    },
    methods:{
        resetBoard(){
            if(!this.baseBoard)
            {
                this.board = new Board();
            }
            else
            {
                this.board = deepClone(new Board, this.baseBoard);          
                this.board.mtlFile = this.board.mtlFilePath;
                this.board.objFile = this.board.objFilePath;
                this.board.image = this.board.imagePath;
                this.board.previewImage = {url: API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, this.board.previewImagePath])};      

            }
        },
        submitClicked(){
            this.attemptedSubmit = true;
            if(this.board.isValid())
            {
                this.submitting = true;
                const payload = {
                    ...this.board,
                    mtlFile: !this.board.mtlFile.data && !this.board.mtlFile.mime ? null : this.board.mtlFile,
                    objFile: !this.board.objFile.data && !this.board.objFile.mime ? null : this.board.objFile,
                    image: !this.board.image.data && !this.board.image.mime ? null : this.board.image,
                    previewImage: !this.board.previewImage.data && !this.board.previewImage.mime ? null : this.board.previewImage,
                }
                this.$emit('submit-requested', payload);
            }
        },
        genericValidator,
        errorValidator,
        onClickConfirmationPopup(value){
            this.showConfirmationPopup = value;     
            this.$router.push({name: "BoardsList" });
        },
    },
    watch:
    {
        putBoardOutput: function(val)
        {
            if(val)
            {
                this.showConfirmationPopup = true;
            }
        },
        postBoardOutput: function(val)
        {
            if(val)
            {
                this.showConfirmationPopup = true;
            }
        },
        baseBoard: function()
        {
            this.resetBoard();
        },
        alertType: function()
        {
            this.submitting = false;
        },
        alertOutput: function()
        {
            this.submitting = false;

            // Show pop up in case it is a string error
            // In case it is an array, it is shown on the field
            if(typeof this.alertOutput === 'string' || this.alertOutput instanceof String)
            {
                alert(this.alertOutput);
            }
        }
    }
  }
</script> 
 
<style scoped>

    .showDropdown
    {
        display: block !important;
    }

    .input-group-append
    {
      height: fit-content;
    }

    img
    {
        width: 50%;
        height: 50%;
    }

</style> 